import { FirebaseError } from '@firebase/app'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { deleteStorageFile } from 'core/firebaseStorage/deleteStorageFile'
import { uploadFileToInventory } from 'core/inventory/addFileToInventory'
import { deletePhotoPosition, updatePhotoPosition } from 'core/inventory/updatePhotoPosition'
import { db, storage } from 'config/firebase'
import { doc, getDoc } from 'firebase/firestore'
import { getDownloadURL, listAll, ref } from '@firebase/storage'
import { useInventory } from './useInventory'
import { VehiclePhoto, PhotoPosition } from '@otw/models'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import JSZip from 'jszip'

const getAdditionalPhotosForInventoryObject = async (
  storagePath: string,
  inventoryObjectId: string,
  definedInventoryPhotos: VehiclePhoto[],
): Promise<VehiclePhoto[]> => {
  const storageRef = ref(storage, storagePath)
  const photos: VehiclePhoto[] = []
  const storageObjects = await listAll(storageRef)

  for (const obj of storageObjects.items) {
    const url = await getDownloadURL(obj)
    const photoIsPresent = definedInventoryPhotos.some(photo => photo.url === url)

    if (!photoIsPresent) {
      photos.push({
        id: inventoryObjectId,
        name: obj.name,
        fullPath: obj.fullPath,
        url,
      })
    }
  }
  return photos
}

const fetchPhotosForInventoryObject = async (
  collectionName: string,
  inventoryObjectId: string,
  subcollection?: { name: string; parentDocId: string },
  onlyMainPhoto?: boolean,
): Promise<VehiclePhoto[]> => {
  const docRef = subcollection
    ? doc(db, collectionName, subcollection.parentDocId, subcollection.name, inventoryObjectId)
    : doc(db, collectionName, inventoryObjectId)

  const docSnap = await getDoc(docRef)
  if (!docSnap.exists()) {
    console.error('No such document!')
    return []
  }

  const storagePath = subcollection
    ? `${collectionName}/${subcollection.parentDocId}/${subcollection.name}/${inventoryObjectId}`
    : `${collectionName}/${inventoryObjectId}`

  const storageRef = ref(storage, storagePath)

  try {
    const definedPhotos: VehiclePhoto[] = []
    if (onlyMainPhoto) {
      const vehicleObject = docSnap.data()

      if (!vehicleObject || !vehicleObject.photos || !vehicleObject.photos.main) {
        console.info(`No main photo found for document ${inventoryObjectId} in collection ${collectionName}`)
        return []
      }
      const photoFiles = await listAll(storageRef)
      if (photoFiles.items.length === 0) {
        //console.info(`No photos found in storage at ${storagePath}.`)
        return []
      }

      const mainPhotoFileName = vehicleObject.photos.main
      const fullPath = `${storagePath}/${mainPhotoFileName}`
      const fileRef = ref(storage, fullPath)
      try {
        const url = await getDownloadURL(fileRef)
        definedPhotos.push({
          id: inventoryObjectId,
          name: mainPhotoFileName,
          url: url,
          position: 'main',
          fullPath: fullPath,
        })
      } catch (error) {
        console.info('Error getting download URL for main photo', mainPhotoFileName, error)
      }
    } else {
      const additionalPhotos = await getAdditionalPhotosForInventoryObject(
        storagePath,
        inventoryObjectId,
        definedPhotos,
      )

      return [...definedPhotos, ...additionalPhotos]
    }

    return definedPhotos
  } catch (error) {
    if (error instanceof FirebaseError && error.code === 'storage/object-not-found') {
      console.info(`No photos directory found at ${storagePath}.`)
    } else {
      console.error(`Error listing files in ${storagePath}:`, error)
    }
    return []
  }
}

export const usePhotosForInventoryObject = (
  collectionName: "master_inventory" | "shield_inventory" | "imported_vehicles",
  inventoryObjectId: string,
  options?: { enabled?: boolean; onlyMainPhoto?: boolean },
  subcollection?: { name: string; parentDocId: string },
) => {
  const queryClient = useQueryClient()
    const { showSuccess, showError, showInfo, showSnackbar, showWarning } = useSnackbar()
  const { updateDocument } = useInventory(collectionName, subcollection, { dontShowUpdates: true })
  const queryKey = subcollection
    ? [
        'photos',
        collectionName,
        subcollection.parentDocId,
        subcollection.name,
        inventoryObjectId,
        options?.onlyMainPhoto,
      ]
    : ['photos', collectionName, inventoryObjectId, options?.onlyMainPhoto]

  const query = useQuery({
    queryKey,
    queryFn: () =>
      fetchPhotosForInventoryObject(collectionName, inventoryObjectId, subcollection, options?.onlyMainPhoto),
    enabled: options?.enabled !== false && Boolean(inventoryObjectId),
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  })

  const deletePhotosMutation = useMutation({
    mutationFn: async (photosToDelete: VehiclePhoto[]) => {
      for (const photo of photosToDelete) {
        await deleteStorageFile(photo.url)
        await deletePhotoPosition(
          subcollection ? `${collectionName}/${subcollection.parentDocId}/${subcollection.name}` : collectionName,
          inventoryObjectId,
          photo.name,
        )
      }
      // Check if there are photos left, if not, remove the photo_storage_path field
      const photos = await fetchPhotosForInventoryObject(collectionName, inventoryObjectId, subcollection)
      if (photos.length === 0) {
        await updateDocument({ id: inventoryObjectId, data: { photo_storage_path: '' } })
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
    },
  })

  const uploadPhotosMutation = useMutation({
    mutationFn: async (photosToUpload: { file: File; previewBlobUrl: string }[]) => {
      const uploadedPhotos = await Promise.all(
        photosToUpload.map(async ({ file }) => {
          const { name, url } = await uploadFileToInventory(
            subcollection ? `${collectionName}/${subcollection.parentDocId}/${subcollection.name}` : collectionName,
            inventoryObjectId,
            file,
            true,
          )
          return { name, url }
        }),
      )
      // update vehicle to now have photos
      await updateDocument({
        id: inventoryObjectId,
        data: {
          photo_storage_path: subcollection
            ? `${collectionName}/${subcollection.parentDocId}/${subcollection.name}/${inventoryObjectId}`
            : `${collectionName}/${inventoryObjectId}`,
        },
      })

      return uploadedPhotos
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
    },
  })

  const updatePositionMutation = useMutation({
    mutationFn: async (params: { name: string; position: PhotoPosition }) => {
      await updatePhotoPosition(
        subcollection ? `${collectionName}/${subcollection.parentDocId}/${subcollection.name}` : collectionName,
        inventoryObjectId,
        params.name,
        params.position,
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
    },
  })

  // Add new function to download all photos as a ZIP file
  const downloadAllPhotosAsZip = async (vehicleInfo?: { stockNumber?: string; make?: string; model?: string }) => {
    try {
      // Fetch all photos (not just the main one)
      const photos = await fetchPhotosForInventoryObject(collectionName, inventoryObjectId, subcollection, false)
      
      if (!photos || photos.length === 0) {
        showError('No photos available to download')
        return false
      }
      
      showInfo('Preparing photos for download...')
      
      // Create a new ZIP file
      const zip = new JSZip()
      
      // Add each photo to the ZIP
      const photoPromises = photos.map(async (photo, index) => {
        try {
          // Fetch the image
          const response = await fetch(photo.url)
          if (!response.ok) {
            throw new Error(`Failed to fetch image ${photo.name}`)
          }
          
          // Get the blob
          const blob = await response.blob()
          
          // Determine a good filename
          let filename = photo.name
          
          // If it has a position, use that in the filename
          if (photo.position) {
            filename = `${photo.position}_${filename}`
          } else {
            filename = `photo_${index + 1}_${filename}`
          }
          
          // Add to zip
          zip.file(filename, blob)
          return true
        } catch (error) {
          console.error(`Error processing photo ${photo.name}:`, error)
          return false
        }
      })
      
      // Wait for all photos to be processed
      await Promise.all(photoPromises)
      
      // Generate the ZIP file
      const zipBlob = await zip.generateAsync({ type: 'blob' })
      
      // Create a download link
      const downloadUrl = URL.createObjectURL(zipBlob)
      const a = document.createElement('a')
      a.href = downloadUrl
      
      // Create filename from vehicle info
      const fileName = `${vehicleInfo?.stockNumber || 'vehicle'}_${vehicleInfo?.make || ''}_${
        vehicleInfo?.model || ''
      }_photos.zip`
      a.download = fileName.replace(/\s+/g, '_')
      
      // Trigger download
      document.body.appendChild(a)
      a.click()
      
      // Clean up
      document.body.removeChild(a)
      URL.revokeObjectURL(downloadUrl)
      
      showSuccess('Photos downloaded successfully')
      return true
    } catch (error) {
      console.error('Error downloading photos as ZIP:', error)
      showError('Failed to download photos')
      return false
    }
  }

  // Add new function to download selected photos as a ZIP file
  const downloadSelectedPhotosAsZip = async (
    selectedPhotos: VehiclePhoto[],
    vehicleInfo?: { stockNumber?: string; make?: string; model?: string }
  ) => {
    try {
      if (!selectedPhotos || selectedPhotos.length === 0) {
        showError('No photos selected to download')
        return false
      }
      
      showInfo('Preparing selected photos for download...')
      
      // Create a new ZIP file
      const zip = new JSZip()
      
      // Add each selected photo to the ZIP
      const photoPromises = selectedPhotos.map(async (photo, index) => {
        try {
          // Fetch the image
          const response = await fetch(photo.url)
          if (!response.ok) {
            throw new Error(`Failed to fetch image ${photo.name}`)
          }
          
          // Get the blob
          const blob = await response.blob()
          
          // Determine a good filename
          let filename = photo.name
          
          // If it has a position, use that in the filename
          if (photo.position) {
            filename = `${photo.position}_${filename}`
          } else {
            filename = `photo_${index + 1}_${filename}`
          }
          
          // Add to zip
          zip.file(filename, blob)
          return true
        } catch (error) {
          console.error(`Error processing photo ${photo.name}:`, error)
          return false
        }
      })
      
      // Wait for all photos to be processed
      await Promise.all(photoPromises)
      
      // Generate the ZIP file
      const zipBlob = await zip.generateAsync({ type: 'blob' })
      
      // Create a download link
      const downloadUrl = URL.createObjectURL(zipBlob)
      const a = document.createElement('a')
      a.href = downloadUrl
      
      // Create filename from vehicle info
      const fileName = `${vehicleInfo?.stockNumber || 'vehicle'}_${vehicleInfo?.make || ''}_${
        vehicleInfo?.model || ''
      }_selected_photos.zip`
      a.download = fileName.replace(/\s+/g, '_')
      
      // Trigger download
      document.body.appendChild(a)
      a.click()
      
      // Clean up
      document.body.removeChild(a)
      URL.revokeObjectURL(downloadUrl)
      
      showSuccess('Selected photos downloaded successfully')
      return true
    } catch (error) {
      console.error('Error downloading selected photos as ZIP:', error)
      showError('Failed to download selected photos')
      return false
    }
  }

  return {
    ...query,
    deletePhotos: deletePhotosMutation.mutate,
    uploadPhotos: uploadPhotosMutation.mutate,
    updatePosition: updatePositionMutation.mutate,
    isDeleting: deletePhotosMutation.isPending,
    isUploading: uploadPhotosMutation.isPending,
    isUpdating: updatePositionMutation.isPending,
    downloadAllPhotosAsZip,
    downloadSelectedPhotosAsZip,
  }
}
